import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Bubble, Dropdown, DropdownBox, List, Popover} from 'tea-component';
import {useMemoizedFn, useRequest} from 'ahooks';
import {useParams} from 'umi';
import {camelCase, isEmpty} from 'lodash-es';
import { calculateDashboardChart, previewDashboardChartSql } from '@dbl/services/dashboard';
import type { IDashboardChartRequest } from '@dbl/services/dashboard';
import {getDateText, hasEmptyProperty, useDblIntl} from '@/pages/datalab/utils/utils';
import {SYSTEM_NAME} from '@dbl/core/global';
import customDashboatdDownload from '@/pages/datalab/utils/customDownload';
import { preparePercentAxisChartDownloadTable } from '@/pages/datalab/utils/enrichPercentAxisChartDownloadData';
import {
  getScopeFilters,
  hasActiveCompareTime,
} from '@/pages/datalab/components/DashboardLayout/components/DragShell/_utils';
import { ChartType } from '@dbl/core/chart';
import { DateFormatString } from '@dbl/core/date';
import SvgIcon from '@home/components/SvgIcon';
import LineChart from '@customComp/Chart/components/LineChart';
import StackBarChart from '@customComp/Chart/components/StackBarChart';
import AnalysisPieChart from '@customComp/Chart/components/AnalysisPieChart';
import Abnormal from '@home/components/Abnormal';
import BarChart from "@customComp/Chart/components/BarChart";
import './index.scss';
import classNames from "classnames";
import MixedChart from '@customComp/Chart/components/MixedChart';
import {handleExportClick} from "@/pages/datalab/components/DashboardLayout/_utils";
import MapChart from "@customComp/Chart/components/MapChart";
import WordCloudChart from '@customComp/Chart/components/WordCloudChart';
import HorizontalBarChart from '@customComp/Chart/components/HorizontalBarChart';
import TimeCompare from "@/pages/datalab/components/DateComponents/TimeCompare";
import { formatDateRange, getDateRange, type TRelativeTime } from "@dbl/core/date";
import PercentStackBarChart from '@customComp/Chart/components/PercentStackBarChart';
import PercentLineChart from '@customComp/Chart/components/PercentLineChart';
import { getOldChartDisplayConfig, normalizeDataLabelConfig, NULL_UI_DEFAULT } from '@dbl/core/chartEditor';
import ShowFilterContentIcon, {
  FILTER_CONTENT_KEY
} from "@/pages/datalab/components/ChartCardIconButtons/ShowFilterContentIcon";
import {useChartCardError} from '@dbl/components/DashboardLayout/components/DragShell/hooks/useChartCardError';
import {allowChartTypeShowUserFilter} from "@dbl/core/global";
import {getLocaleState} from '@home/utils/state';
import useFullScreen from '@home/components/FullScreenScale/_hooks/useFullScreen';
import {DownloadModal} from './DragCardHeader';
import {useClosePopoversWhenAnchorHidden} from './useClosePopoversWhenAnchorHidden';
import {getCardOverlayProps} from './getCardOverlayProps';
import { usePreviewSQL, PreviewSQLModal } from '@dbl/domainComponents/dataSource';

const ChartDataKey = {
  [ChartType.EventLine]: 'event_line_chart',
  [ChartType.Line]: 'line_chart',
  [ChartType.Bar]: 'bar_charts',
  [ChartType.StackBar]: 'stack_bar_chart',
  [ChartType.Pie]: 'chart_sum_data',
  [ChartType.MixedChart]: 'mixed_chart',
  [ChartType.WordCloud]: 'word_cloud',
  [ChartType.HorizontalBarChart]: 'horizontal_bar_charts',
  [ChartType.PercentStackBar]: 'percent_stack_bar_chart',
  [ChartType.PercentLine]: 'percent_line_chart',
};

// 统一维护需要走后端下载弹窗的图表类型。
const BACKEND_DOWNLOAD_CHART_TYPES = [
  ChartType.Line,
  ChartType.Bar,
  ChartType.MixedChart,
  ChartType.Map,
  ChartType.WordCloud,
];


const ChartTragCard = (props: any) => {
  const {
    deleteCard,
    handleOnSubmit,
    dataMsg,
    filterValues,
    userFilter,
    isEdit,
    hasChartPrivilege,
    system = 'datalab',
    chartExplore,
    // onForceClose,
    chartsInfoLoading,
    chartType,
    chartSwitchNode,
    isActive = true,
    shouldRequest = true,
    switchRequestId = 0,
    cacheKey,
    onTypeDataLoaded,
    onCompareTimeFiltersChange,
    tableDownloadParams,
    tooltipMinTopPx,
    layoutGridColSpan: _layoutGridColSpan,
  } = props;
  const {task_id: taskId} = dataMsg || {};
  const [open, setOpen] = useState(false);
  const [openVs, setOpenVs] = useState(false);
  const [openDownload, setOpenDownload] = useState(false);
  // 事件详情
  const [isEvent, setIsEvent] = useState(true);
  
  const fullScreenContent = useRef<HTMLDivElement>(null);
  const [previewMoreEntryEl, setPreviewMoreEntryEl] = useState<HTMLDivElement | null>(null);
  const {isFullScreen, requestFullScreen, exitFullScreen} = useFullScreen(fullScreenContent);
  const downloadParamsRef = useRef<IDashboardChartRequest | Record<string, never>>({});

  const closePreviewPopovers = useMemoizedFn(() => {
    setOpenVs(false);
    setOpen(false);
    fullScreenContent.current?.classList.remove('dbl-show-card-icon');
  });

  const closeTimeCompareAndMenu = closePreviewPopovers;

  /** 预览态三点菜单：时间对比打开时，鼠标移出菜单也不收起 */
  const handlePreviewDropdownOpenChange = useMemoizedFn((nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      fullScreenContent.current?.classList.add('dbl-show-card-icon');
      return;
    }
    if (openVs) {
      setOpen(true);
      return;
    }
    setOpen(false);
    fullScreenContent.current?.classList.remove('dbl-show-card-icon');
  });

  useClosePopoversWhenAnchorHidden(
    previewMoreEntryEl,
    !isEdit && (open || openVs),
    closePreviewPopovers,
  );
  
  // 经分承接页gameCode
  const {gamecode}: any = useParams();
  // const gameCode = useSelector(state => {
  //   // @ts-ignore
  //   return state.datalab.gameCode
  // }, shallowEqual) || dashboardGameCode;
  const {commonFormatMessage, isEn, dblFormatMessage,formatMessage} = useDblIntl('create_dashboard');

  const buildChartParams = useMemoizedFn((queries?: Record<string, unknown>): IDashboardChartRequest => ({
    game_code: gamecode,
    task_id: [String(taskId)],
    queries: {
      filters: filterValues,
      user_filter: userFilter,
      ...queries,
    },
    map_type: chartType === ChartType.Map ? 'world' : undefined,
    system,
    cache_key: cacheKey,
    is_sql_preview: false,
  }));

  const resolvePreviewSqlParams = useMemoizedFn((): IDashboardChartRequest => {
    if (tableDownloadParams && Object.keys(tableDownloadParams).length > 0) {
      return tableDownloadParams as IDashboardChartRequest;
    }
    if (!isEmpty(downloadParamsRef.current)) {
      return downloadParamsRef.current as IDashboardChartRequest;
    }
    return buildChartParams();
  });

  const {
    previewSQL,
    loading: previewSqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql: () =>
      previewDashboardChartSql(chartType, resolvePreviewSqlParams()),
  });

  // 原按 layoutGridColSpan（如 ≤4 列）区分预览「窄卡仅三点 / 宽卡行内图标」，已统一为「全屏 + 更多 + 悬停显隐」；宽窄实现可自 git 历史查看。
  // const isCompactViewToolbar = Boolean(
  //   !isEdit && typeof layoutGridColSpan === 'number' && layoutGridColSpan <= 4
  // );

  useEffect(() => {
    setOpen(false);
  }, [isEdit]);

  const requestData = useMemoizedFn((queries?: Record<string, unknown>) => {
    const params = buildChartParams(queries);
    downloadParamsRef.current = params;
    return calculateDashboardChart(chartType, params);
  });
  // fix：该状态用于解决底层echarts组件获取不到实例问题，导致插件功能失效
  const [cardData, setCardData] = useState<any>({});
  // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求
  const {data, loading, error, run: getData}: any = useRequest(requestData, {
    manual: true,
    onError: (e) => {
      console.error('报表中图表计算失败: ', e);
    },
    onSuccess: (res) => {
      setCardData(res);
      onTypeDataLoaded?.(chartType);
    }
  });
  
  // 扩展支持图表类型切换时, 使用该卡片时, 需要传入 isActive, shouldRequest, switchRequestId, cacheKey, onTypeDataLoaded 参数
  // isActive: 是否激活, 默认激活
  // shouldRequest: 是否需要请求数据, 默认需要
  // switchRequestId: 切换请求id, 默认0
  // cacheKey: 缓存key
  // onTypeDataLoaded: 类型数据加载完成回调
  useEffect(() => {
    if (!isActive) return;
    if (!shouldRequest) return;
    if (chartsInfoLoading) return;
    if (!filterValues && !userFilter?.controls?.length) return;
    getData();
  }, [filterValues, chartsInfoLoading, userFilter, isActive, shouldRequest, switchRequestId]);
  
  // 因为 图表组装echarts的option时, 依赖的都是 接口返回的data数据, 所以所有依赖data数据的信息转换都放在同一个useMemo内部, 保证状态更新的同步.
  const {chartData, curMetricsName, downLoadConfig, condition, curMetrics, title, metrics, dimensions, metricIsAllPercent, aggregation, chartConfig, metricInfo, isNoData, fieldInfos, scopeFilters, userFilterInfo, pieChartTitle, compareTimeData} = useMemo(() => {
    const chartData = data?.[taskId] || [];
    // const newFlag = chartData?.new_flag;
    const title = isEn ? dataMsg?.name : (dataMsg?.name_cn || dataMsg?.name);
    // 后端返回什么就下载什么, 不做额外的中英文翻译
    const downLoadConfig = {
      columnName: chartData?.column_name || [],
      data: chartData?.table_data || [],
    };
    const dimensions = chartData?.column_name?.filter((v: any) => v?.type === 1);
    const chartDetailFilter = chartData?.chart_detail ? JSON.parse(chartData?.chart_detail?.filter || "{}") : {};
    const condition = {
      group_by: chartDetailFilter?.group_by || [],
      filters: chartDetailFilter?.filters || {},
      metrics: chartDetailFilter?.metrics || [],
      orderby: [],
    };
    
    const metricIsAllPercent = condition.metrics?.reduce((acc, item) => acc && item.column_type === 'percent', true);
    
    // curMetrics兜底处理
    const statisticalMetrics = dataMsg?.statistical_metrics || '';
    let curMetrics = statisticalMetrics;
    if (!chartData?.metrics?.includes(statisticalMetrics)) {
      curMetrics = chartData?.metrics?.[0];
    }
    // 和后端核对后先用metrics做匹配，metrics_en 会不准
    const curMetricsIndex = data?.[taskId]?.metrics?.findIndex(item =>
      // 和凡平核对后不能直接两者相等取值，因为历史数据中statisticalMetrics可能会比item多一些后缀，如：技计数等
      statisticalMetrics?.startsWith(item?.trim())
    );
    // let curMetricsName = isEn ? data?.[taskId]?.metrics_en?.[curMetricsIndex] : (data?.[taskId]?.metrics?.[curMetricsIndex] || data?.[taskId]?.metrics_en?.[curMetricsIndex]);
    const curMetricsName = data?.[taskId]?.metrics?.[curMetricsIndex]?.replace(/(t[0-2]_)/, '') || '';
    
    /**
     * 获取图表配置项
     * config 是所有图表配置项的顶层结构
     * rightConfig 是 拖拉拽页面右侧面板的配置项
     */
    
    const config = JSON.parse(chartData?.chart_detail?.column_infos || '{}'); // 有时会返回空字符串, 所以使用 ||
    const rightConfig = config?.chartsCfgSave ?? {};
    const formatChartType = chartType === ChartType.StackBar ? 'stackChart' : camelCase(chartType);
    const {
      showValues = 0,
      showValuesIndexs = [0],
      yAxisName = '',
      yAxisRangeSwitch = false,
      reference_line: referenceLine,
      map_colors: mapColors,
      x_label: xLabel,
      colorSetting,
      freeze,
      xAxisName,
      showDataLabel: rawShowDataLabel,
      displayForm: rawDisplayForm,
      labelContent: rawLabelContent,
      tooltipSetting,
      stackedDynamicState,
      legend_position: legendPosition,
      legend_colors: legendColors,
      null_ui: nullUi,
      dualYAxisEnabled,
      primaryYAxis,
      secondYAxis,
    } = rightConfig[chartType]?.customizeSetting?.[formatChartType] ?? {};

    const {
      showDataLabel,
      displayForm,
      labelContent,
    } = normalizeDataLabelConfig(
      {
        showValues,
        showDataLabel: rawShowDataLabel,
        displayForm: rawDisplayForm,
        labelContent: rawLabelContent,
      },
      chartType,
    );
    
    let isNoData = false;
    if (chartType === ChartType.MixedChart)   {
      isNoData = hasEmptyProperty(chartData[ChartDataKey[chartType]], ['series_data', 'x_axis_data']);
    } else if (chartType === ChartType.Map) {
      isNoData = false;
      // 地图没有数据也要展示地图
    } else if (chartType === ChartType.WordCloud) {
      isNoData = !chartData[ChartDataKey[chartType]]?.data?.length;
    } else if (chartType === ChartType.HorizontalBarChart) {
      isNoData = hasEmptyProperty(chartData[ChartDataKey[chartType]]?.[0], ['series', 'yAxis']);
    } else if ([ChartType.Line, ChartType.EventLine, ChartType.Bar, ChartType.StackBar, ChartType.PercentStackBar, ChartType.PercentLine].includes(chartType)) {
      isNoData = !chartData[ChartDataKey[chartType]]?.series_data;
    } else {
      isNoData = !chartData[ChartDataKey[chartType]]?.[0];
    }
    // 兼容旧图表的 accuracy 和 unit
    const fieldInfos = chartData?.column_name?.map(column => {
      const { accuracy, unit } = getOldChartDisplayConfig({...column, column_type: column.column_date_type}, chartType);
      return {...column, accuracy, unit};
    }) || [];
    
    // 获取scope的筛选器信息
    const allFilters = chartData?.filters || [];
    const dateFilters = allFilters?.filter(item => item.column_type === 'date') || [];
    // 和后端确认过, is_vs_compare_time 为true时, 代表是vs图标中的base time 筛选器字段
    // const compareFilter = dateFilters.find(item => !isEmpty(item?.compare_time));
    const compareFilter = dateFilters?.find(item => item?.is_vs_compare_time);
    const baseTimeFilter = compareFilter || dateFilters[0];
    let compareTimeData = {};
    if (baseTimeFilter) {
      compareTimeData = {
        filterId: baseTimeFilter?.filterKey || baseTimeFilter?.table_id,
        defaultCompareTimes: Array.isArray(baseTimeFilter?.compare_time) ? baseTimeFilter?.compare_time : [],
        baseTimeRange: Array.isArray(baseTimeFilter?.value) ? baseTimeFilter?.value : [],
        baseTimeName: baseTimeFilter?.filter_name || baseTimeFilter?.display_name || baseTimeFilter?.column,
        timeGranularity: baseTimeFilter?.dateType,
        chartFilter: baseTimeFilter,
      };
    } else {
      // 说明不存在 日期筛选器控制图表, 此时不显示vs图标
      compareTimeData = {};
    }
    
    let pieChartTitle = [];
    // 如果是饼图, 需要计算浮窗左上角时间范围
    if (chartType === ChartType.Pie) {
      const timeFilters = allFilters?.filter(item => item?.column_type === "date");
      const dashboardDateFilter = timeFilters?.find(item => item?.filter_name); // 找到报表日期筛选器
      if (dashboardDateFilter) {
        const {dateType, last_day} = dashboardDateFilter;
        const dateRange = dashboardDateFilter.value;
        // @ts-ignore
        const dateFormat = DateFormatString[dashboardDateFilter?.to_date_format || 'YYYYMMMDD'];
        const lastCount = dateRange[0]?.startsWith('-1') ? -1 : (dateRange[0]?.split('-')?.[0] || 7);
        const timeRange = getDateText({dateType, dateRange, dateFormat, lastCount, from: last_day});
        const title = timeRange?.join(' ~ ') || '';
        pieChartTitle = [title, title]; // 兼容饼图对比项
      } else {
        // 从图表中获取时间范围
        const dataMsgFilters = JSON.parse(dataMsg?.filter || '{}');
        const filtersArr = dataMsgFilters?.filters?.filters || [];
        // 当前需求饼图最多有两个， 即最多只有一个对比项，所以直接取第一项，多个的后续优化，现有后端的结构感觉有问题，所以不在此扩展处理
        const filterCompares = dataMsgFilters?.filter_compares?.[0]?.filters || [];
        const timeFilter = filtersArr?.filter((item: any) =>  item?.column_type === "date") || [];
        const compareDateFilter = filterCompares?.filter((item: any) =>  item?.column_type === "date") || [];
        const timeRangeVal = [];
        if (timeFilter?.length > 0) {
          const dateType = timeFilter?.[0]?.dateType;
          const dateRange = timeFilter?.[0]?.value;
          const from = timeFilter?.[0]?.last_day;
          // @ts-ignore
          const dateFormat = DateFormatString[timeFilter?.[0]?.to_date_format || 'YYYYMMMDD'];
          const timeRange = getDateText({dateType, dateRange, dateFormat, lastCount: timeFilter?.[0]?.lastCount, from});
          timeRangeVal.push(timeRange?.join(' ~ ') || '');
        }
        
        if(compareDateFilter?.length > 0) {
          // eslint-disable-next-line @typescript-eslint/prefer-for-of
          for(let i=0;i<compareDateFilter.length;i++) {
            const item = compareDateFilter[i];
            const dateType = item?.dateType;
            const dateRange = item?.value;
            const from = item?.last_day;
            // @ts-ignore
            const dateFormat = DateFormatString[item?.to_date_format || 'YYYYMMMDD'];
            
            const timeRange = getDateText({dateType, dateRange, dateFormat, lastCount: item?.lastCount, from});
            timeRangeVal.push(timeRange?.join(' ~ ') || '');
          }
        }
        pieChartTitle = timeRangeVal;
        
      }
    }
    
    // 找到第一个aggregate有值的元素, aggregate 主要用于堆叠图的浮窗内部是否显示Total判断.
    const aggregation = chartData?.column_name?.find((v: any) => v?.aggregate)?.aggregate;
    
    return {
      chartData,
      title,
      curMetricsName,
      downLoadConfig,
      condition,
      curMetrics,
      metrics: chartData.metrics,
      dimensions,
      metricIsAllPercent,
      // newFlag,
      chartConfig: {
        showValues,
        showValuesIndexs,
        yAxisName,
        yAxisRangeSwitch,
        referenceLine,
        mapColors,
        xLabel,
        colorSetting,
        freeze,
        xAxisName,
        showDataLabel,
        displayForm,
        labelContent,
        tooltipSetting,
        stackedDynamicState,
        legendPosition,
        legendColors,
        nullUi: nullUi ?? NULL_UI_DEFAULT,
        dualYAxisEnabled,
        primaryYAxis,
        secondYAxis,
      },
      isNoData,
      metricInfo: fieldInfos?.find((opt: {name: string;}) => opt.name === curMetrics) || {},
      fieldInfos,
      scopeFilters: getScopeFilters(allFilters) ?? [],
      userFilterInfo: chartData?.user_filter ?? {controls: []},
      pieChartTitle,
      compareTimeData,
      aggregation
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataMsg, cardData, isEn]);
  
  const componentItemObj: any = {
    [ChartType.EventLine]: (
      <LineChart
        tooltipMinTopPx={tooltipMinTopPx}
        isEvent={isEvent}
        eventData={chartData?.event_list || []}
        chartData={{
          xAxisData: chartData?.event_line_chart?.x_axis_data || [],
          seriesData: chartData?.event_line_chart?.series_data || [],
        }}
        metricNames={metrics || []}
        dimensions={condition?.group_by || []}
        isOnlyOneDimension={condition?.group_by?.length <= 1}
        metricIsAllPercent={metricIsAllPercent}
        chartConfig={chartConfig}
        compareDateCount={compareTimeData.defaultCompareTimes?.length || 0}
        fieldInfos={fieldInfos}
      />
    ),
    [ChartType.Line]: (
      <LineChart
        tooltipMinTopPx={tooltipMinTopPx}
        chartData={{
          xAxisData: chartData?.line_chart?.x_axis_data || [],
          seriesData: chartData?.line_chart?.series_data || [],
        }}
        metricNames={metrics || []}
        dimensions={condition?.group_by || []}
        isOnlyOneDimension={condition?.group_by?.length <= 1}
        metricIsAllPercent={metricIsAllPercent}
        chartConfig={chartConfig}
        compareDateCount={compareTimeData.defaultCompareTimes?.length || 0}
        fieldInfos={fieldInfos}
        userFilterControls={userFilterInfo?.controls ?? []}
      />
    ),
    [ChartType.Bar]: (
      <BarChart
        tooltipMinTopPx={tooltipMinTopPx}
        chartData={{
          xAxisData: chartData?.bar_charts?.x_axis_data || [],
          seriesData: chartData?.bar_charts?.series_data || [],
        }}
        metricNames={metrics || []}
        dimensions={condition?.group_by || []}
        isOnlyOneDimension={condition?.group_by?.length <= 1}
        metricIsAllPercent={metricIsAllPercent}
        chartConfig={chartConfig}
        compareDateCount={compareTimeData.defaultCompareTimes?.length || 0}
        fieldInfos={fieldInfos}
        userFilterControls={userFilterInfo?.controls ?? []}
      />
    ),
    [ChartType.HorizontalBarChart]: (
      <HorizontalBarChart
        tooltipMinTopPx={tooltipMinTopPx}
        chartData={chartData?.horizontal_bar_charts || []}
        dimensions={condition?.group_by || []}
        metricNames={metrics || []}
        isOnlyOneDimension={condition?.group_by?.length <= 1}
        chartConfig={chartConfig}
        fieldInfos={fieldInfos}
        metricIsAllPercent={metricIsAllPercent}
      />
    ),
    [ChartType.StackBar]: (
      <StackBarChart
        tooltipMinTopPx={tooltipMinTopPx}
        chartData={{
          xAxisData: chartData?.stack_bar_chart?.x_axis_data || [],
          seriesData: chartData?.stack_bar_chart?.series_data || [],
        }}
        metricInfo={metricInfo}
        incrementSum={['sum', 'count', 'distinct'].includes(aggregation)}
        // curMetrics={curMetrics}
        curMetricsName={curMetricsName}
        // dimensions={condition?.group_by || []}
        chartConfig={chartConfig}
        metricIsAllPercent={metricIsAllPercent}
        fieldInfos={fieldInfos}
        metricNames={metrics || []}
        // isOnlyOneDimension={condition?.group_by?.length <= 1}
      />
    ),
    [ChartType.PercentStackBar]: (
      <PercentStackBarChart
        chartData={{
          xAxisData: chartData?.percent_stack_bar_chart?.x_axis_data || [],
          seriesData: chartData?.percent_stack_bar_chart?.series_data || [],
        }}
        metricInfo={metricInfo}
        metrics={chartData?.metrics || []}
        dimensions={condition?.group_by || []}
        chartConfig={chartConfig}
      />
    ),
    [ChartType.PercentLine]: (
      <PercentLineChart
        chartData={{
          xAxisData: chartData?.percent_line_chart?.x_axis_data || [],
          seriesData: chartData?.percent_line_chart?.series_data || [],
        }}
        metrics={chartData?.metrics || []}
        dimensions={condition?.group_by || []}
        chartConfig={chartConfig}
      />
    ),
    [ChartType.Pie]: (
      <AnalysisPieChart
        condition={condition}
        pieChartTitle={pieChartTitle}
        chartData={chartData?.chart_sum_data || []}
        curMetrics={curMetrics}
        curMetricsName={curMetricsName}
        metricInfo={metricInfo}
        chartConfig={chartConfig}
      />
    ),
    [ChartType.MixedChart]: (
      <MixedChart
        chartData={chartData?.mixed_chart || {}}
        metricNames={metrics || []}
        chartConfig={chartConfig}
        fieldInfos={fieldInfos}
      />
    ),
    [ChartType.Map]: (
      <MapChart
        chartData={chartData?.map_charts?.[0] || {}}
        metricInfo={metricInfo}
        chartConfig={chartConfig}
      />
    ),
    [ChartType.WordCloud]: (
      <WordCloudChart
        chartData={chartData?.word_cloud?.data || []}
        // map逻辑是为了兼容旧图表的accuracy, unit设置
        metricInfo={chartData?.word_cloud?.metric_info?.map(column => {
          const { accuracy, unit } = getOldChartDisplayConfig({...column, column_type: column.value_type}, chartType);
          return {...column, accuracy, unit};
        }) || []}
        dimensions={dimensions}
        chartConfig={chartConfig}
      />
    ),
  };
  
  const jumpToEdit = () => {
    // 重置离开弹窗操作在最外层index中
    // dispatch({
    //   type: 'customDashboard/save',
    //   payload: {
    //     isHideCustomLeaveModal: true,
    //   }
    // })
    const pathId = dataMsg.type === 1 ? 'Action/ExploreAnalysis' : 'Analysis/MultiAnalysis';
    const path = `/${SYSTEM_NAME}/${gamecode}/${pathId}?from=edit&taskId=${dataMsg.task_id}&name=${dataMsg?.name}&nameCn=${dataMsg?.name_cn}`;
    
    handleOnSubmit?.(path);
  };
  
  const downLoadFunc = () => {
    if (BACKEND_DOWNLOAD_CHART_TYPES.includes(chartType)) {
      setOpenDownload(true);
      return;
    }
    const preparedDownloadConfig = preparePercentAxisChartDownloadTable({
      chartType,
      data: downLoadConfig.data,
      columnName: downLoadConfig.columnName,
    });
    // 替换 `% /` 为 `_`
    customDashboatdDownload(
      preparedDownloadConfig,
      title?.replace(/[/%]/g, '_'),
      isEn,
    );
  };
  
  const failedMsg = useChartCardError(error);
  // 图表切换使用表格的下载参数（sort影响下载）
  const finalDownloadParams = tableDownloadParams && Object.keys(tableDownloadParams).length > 0
    ? tableDownloadParams
    : downloadParamsRef.current;

  const overlayProps = getCardOverlayProps(
    isFullScreen,
    fullScreenContent.current,
  );
  const isTimeCompareActive = useMemo(
    () => hasActiveCompareTime(compareTimeData, filterValues),
    [compareTimeData, filterValues],
  );
  const chartHeaderBusy = loading || error || chartsInfoLoading;
  const chartFullScreenState = isFullScreen ? true : chartHeaderBusy || isNoData ? undefined : false;

  return (
    <div
      ref={fullScreenContent}
      className={classNames(
        `dbl-fullscreen dbl-fullscreen-${taskId} dbl-card-drag table-analysis-chart`,
        {'dbl-fullscreen-context': isFullScreen}
      )}
    >
      <div className="dbl-card-drag-flex">
        <div className="dbl-card-drag-title">
          <div className="dbl-card-drag-title__row">
            {isEdit && <SvgIcon type="drag-icon"/>}
            <span className="dbl-card-drag-title__text">{title}</span>
          </div>
        </div>
        <div className={classNames('dbl-card-icon-operation-box', 'dbl-card-preview-toolbar')}>
          {!chartHeaderBusy && !isFullScreen ? (
            <Bubble dark content={formatMessage({id: 'dbl_common_fullscreen'})} {...overlayProps}>
              <div
                className={classNames('dbl-card-icon-download', {'dbl-card-icon-disabled': chartFullScreenState === undefined})}
                onClick={() => {
                  if (chartFullScreenState === undefined) return;
                  getLocaleState('datalabMessageBus')?.notifyTableFullscreenClick({operation: 'fullScreen'});
                  requestFullScreen();
                }}
              >
                <SvgIcon type="fullscreen"/>
              </div>
            </Bubble>
          ) : null}
          {isFullScreen ? (
            <div
              className="dbl-card-icon-close"
              onClick={() => {
                getLocaleState('datalabMessageBus')?.notifyTableFullscreenClick({operation: 'notFullScreen'});
                exitFullScreen();
              }}
            >
              <SvgIcon type="clonse"/>
            </div>
          ) : null}
          {!chartHeaderBusy ? (
            <Dropdown
              trigger="hover"
              clickClose={false}
              boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
              button={
                <div
                  ref={setPreviewMoreEntryEl}
                  className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}
                >
                  <SvgIcon type="there-points"/>
                </div>
              }
              placementOffset={0}
              open={open}
              appearance="pure"
              placement="bottom-end"
              onOpenChange={isEdit ? setOpen : handlePreviewDropdownOpenChange}
              {...overlayProps}
            >
              <List type="option">
                {isEdit ? (
                  <Fragment>
                    {chartSwitchNode ? (
                      <List.Item className="dbl-card-preview-chart-switch-menu-item">
                        {chartSwitchNode}
                      </List.Item>
                    ) : null}
                    {!chartHeaderBusy ? (
                      <List.Item className="dbl-card-preview-filter-menu-item">
                        <ShowFilterContentIcon
                          contentMap={{
                            [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                            [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                          }}
                          isEdit={true}
                          overlayClassName="dbl-card-preview-more-filter-overlay"
                          triggerEndContent={
                            <span className="dbl-card-preview-more-menu-label">
                              {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                            </span>
                          }
                          onVisibleChange={(visible: boolean) => {
                            if (visible) {
                              fullScreenContent.current?.classList.add('dbl-show-card-icon');
                            } else {
                              fullScreenContent.current?.classList.remove('dbl-show-card-icon');
                            }
                          }}
                          {...overlayProps}
                        />
                      </List.Item>
                    ) : null}
                    {(hasChartPrivilege && dataMsg?.edit) ? (
                      <List.Item
                        onClick={() => {
                          jumpToEdit();
                          setOpen(false);
                        }}
                      >
                        <SvgIcon type="pencil-m"/>{commonFormatMessage('edit')}
                      </List.Item>
                    ) : null}
                    <List.Item
                      onClick={() => {
                        previewSQL();
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="open-eyes"/>{formatMessage({id: 'dbl_multi_preview_sql'})}
                    </List.Item>
                    <List.Item
                      onClick={() => {
                        setOpen(false);
                        deleteCard(dataMsg);
                      }}
                    >
                      <SvgIcon type="trash-can-icon"/>{commonFormatMessage('delete')}
                    </List.Item>
                    {chartType === ChartType.EventLine ? (
                      <List.Item
                        className="dbl-card-preview-event-toggle-menu-item"
                        onClick={() => {
                          setIsEvent(!isEvent);
                          setOpen(false);
                        }}
                      >
                        <SvgIcon type={isEvent ? 'move-left' : 'move-right'}/>
                        {isEvent ? formatMessage({id: 'common_show_event'}) : formatMessage({id: 'common_hide_event'})}
                      </List.Item>
                    ) : null}
                    <List.Item
                      className="dbl-card-preview-download-menu-item"
                      onClick={() => {
                        downLoadFunc();
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="download"/>
                      {commonFormatMessage('download')}
                    </List.Item>
                  </Fragment>
                ) : (
                  <Fragment>
                    {chartSwitchNode ? (
                      <List.Item className="dbl-card-preview-chart-switch-menu-item">
                        {chartSwitchNode}
                      </List.Item>
                    ) : null}
                    {([ChartType.Line, ChartType.EventLine, ChartType.Bar].includes(chartType)) && compareTimeData?.filterId ? (
                      <Popover
                        trigger="click"
                        placement="right-start"
                        visible={openVs}
                        onVisibleChange={(vsOpen) => {
                          if (vsOpen) {
                            setOpenVs(true);
                            fullScreenContent.current?.classList.add('dbl-show-card-icon');
                          }
                          // 不响应 false：仅能通过确定/取消关闭，避免移出菜单或误点外部被收起
                        }}
                        {...overlayProps}
                        overlay={
                          <DropdownBox
                            className={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-timecompare-dropdown')}
                            preventMouseDown
                          >
                            <TimeCompare
                              openSingle={true}
                              defaultCompareTimes={compareTimeData.defaultCompareTimes}
                              baseTimeRange={compareTimeData.baseTimeRange}
                              baseTimeName={compareTimeData.baseTimeName}
                              timeGranularity={compareTimeData.timeGranularity}
                              dropdownProps={overlayProps}
                              popoverProps={overlayProps}
                              onConfirm={(compareTimes: TRelativeTime[]) => {
                                let newCompareTimes: TRelativeTime[] = [];
                                if (compareTimes && compareTimes.length > 0) {
                                  newCompareTimes = compareTimes.map((compare) => {
                                    if (compare?.time_range && isEmpty(compare.last_time)) return compare;
                                    const {last_time: lastTime, granularity} = compare || ({} as any);
                                    const range = getDateRange(compareTimeData.baseTimeRange || [], {
                                      amount: lastTime,
                                      granularity,
                                      timeGranularity: compareTimeData.timeGranularity,
                                    });
                                    return {
                                      ...compare,
                                      time_range: formatDateRange(range, compareTimeData.timeGranularity),
                                    } as any;
                                  });
                                }

                                if (compareTimeData?.chartFilter?.filter_name) {
                                  const newFilters = filterValues?.map((item: any) => {
                                    if (item.filterKey === compareTimeData.filterId) {
                                      return {
                                        ...item,
                                        compare_time: newCompareTimes
                                      };
                                    }
                                    return item;
                                  });
                                  if (onCompareTimeFiltersChange) {
                                    onCompareTimeFiltersChange?.(newFilters);
                                  } else {
                                    getData({filters: newFilters});
                                  }
                                } else {
                                  const scopeCharts = filterValues?.find((item: any) => item?.filterKey === compareTimeData?.filterId)?.scope_charts || [];
                                  const nextFilters = [
                                    ...(filterValues || []),
                                    {
                                      ...(compareTimeData?.chartFilter || {}),
                                      compare_time: newCompareTimes,
                                      scope_charts: scopeCharts,
                                    }
                                  ];
                                  if (onCompareTimeFiltersChange) {
                                    onCompareTimeFiltersChange?.(nextFilters);
                                  } else {
                                    getData({filters: nextFilters});
                                  }
                                }

                                closeTimeCompareAndMenu();
                              }}
                              onCancel={closeTimeCompareAndMenu}
                            />
                          </DropdownBox>
                        }
                      >
                        <List.Item
                          className={classNames(
                            'dbl-card-preview-vs-menu-item',
                            isTimeCompareActive && 'dbl-card-preview-vs-menu-item--active',
                          )}
                        >
                          <SvgIcon type="vs-icon"/>
                          <span className="dbl-card-preview-vs-menu-text">
                            {formatMessage({id: 'dbl_time_compare_title'})}
                          </span>
                        </List.Item>
                      </Popover>
                    ) : null}
                    <List.Item className="dbl-card-preview-filter-menu-item">
                      <ShowFilterContentIcon
                        contentMap={{
                          [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                          [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                        }}
                        isEdit={true}
                        overlayClassName="dbl-card-preview-more-filter-overlay"
                        triggerEndContent={
                          <span className="dbl-card-preview-more-menu-label">
                            {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                          </span>
                        }
                        onVisibleChange={(visible: boolean) => {
                          if (visible) {
                            fullScreenContent.current?.classList.add('dbl-show-card-icon');
                          } else {
                            fullScreenContent.current?.classList.remove('dbl-show-card-icon');
                          }
                        }}
                        {...overlayProps}
                      />
                    </List.Item>
                    {chartType === ChartType.EventLine ? (
                      <List.Item
                        className="dbl-card-preview-event-toggle-menu-item"
                        onClick={() => {
                          setIsEvent(!isEvent);
                          setOpen(false);
                        }}
                      >
                        <SvgIcon type={isEvent ? 'move-left' : 'move-right'}/>
                        {isEvent ? formatMessage({id: 'common_show_event'}) : formatMessage({id: 'common_hide_event'})}
                      </List.Item>
                    ) : null}
                    {
                      chartExplore && (dataMsg.type === 0) ? (
                        <List.Item
                          className="dbl-card-preview-explore-menu-item"
                          onClick={() => {
                            handleExportClick({
                              filters: data?.[taskId]?.filters,
                              system,
                              gamecode,
                              dataMsg,
                              userFilterInfo: allowChartTypeShowUserFilter({chartType}) ? userFilterInfo : undefined,
                            });
                            setOpen(false);
                          }}
                        >
                          <SvgIcon type="explore"/>
                          {dblFormatMessage('icon_explore')}
                        </List.Item>
                      ) : null
                    }
                    <List.Item
                      onClick={() => {
                        previewSQL();
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="open-eyes"/>{formatMessage({id: 'dbl_multi_preview_sql'})}
                    </List.Item>
                    <List.Item
                      className="dbl-card-preview-download-menu-item"
                      onClick={() => {
                        downLoadFunc();
                      }}
                    >
                      <SvgIcon type="download"/>
                      {commonFormatMessage('download')}
                    </List.Item>
                  </Fragment>
                )}
              </List>
            </Dropdown>
          ) : null}

        
        </div>
      </div>
      <div className="dbl-card-drag-chart-body">
        <Abnormal
          style={{ height: '100%', width: '100%' }}
          isLoading={loading || chartsInfoLoading}
          isError={error}
          nodataMsg={commonFormatMessage("no_filter_data")}
          failedMsg={failedMsg}
          isNoData={isNoData}
          component={componentItemObj?.[chartType] ? componentItemObj?.[chartType] : ''}
        />
      </div>
      {(BACKEND_DOWNLOAD_CHART_TYPES.includes(chartType)) ? (
        <DownloadModal
          open={openDownload}
          closeDownloadModal={() => setOpenDownload(false)}
          formatMessage={formatMessage}
          downloadParams={finalDownloadParams}
          dataMsg={dataMsg}
          isEn={isEn}
        />
      ) : ''}

      {/* SQL 预览弹窗 */}
      <PreviewSQLModal
        visible={previewSqlModalVisible}
        onClose={hidePreviewSqlModal}
        sql={previewSqlContent}
        loading={previewSqlLoading}
        error={previewSqlError}
      />
    
    </div>
  );
};

export default ChartTragCard;
